import React from 'react'
import { checkPermission, checkTranslation } from '../../utils/useful'
import {
    canEditProcessStage,
    canManageProcessStage,
    canViewProcessStage,
} from '../../utils/processStageAccess'
import './ProcessRoadmap.css'

class ProcessRoadmap extends React.Component {
    setContainerRef = (element) => {
        this.container = element
    }

    setCurrentRef = (element) => {
        this.currentNode = element
    }

    componentDidMount() {
        this.scrollToCurrent()
    }

    componentDidUpdate(prevProps) {
        if (prevProps.currentStageKey !== this.props.currentStageKey) {
            this.scrollToCurrent()
        }
    }

    scrollToCurrent = () => {
        window.setTimeout(() => {
            if (!this.container || !this.currentNode) return
            const containerRect = this.container.getBoundingClientRect()
            const currentRect = this.currentNode.getBoundingClientRect()
            const horizontalOffset = currentRect.left
                - containerRect.left
                - ((containerRect.width - currentRect.width) / 2)

            // Keep the page itself stationary when several embedded roadmaps
            // mount at once. scrollIntoView also scrolls ancestor containers.
            this.container.scrollBy({
                left: horizontalOffset,
                behavior: 'smooth',
            })
        }, 80)
    }

    scroll = (direction) => {
        if (!this.container) return
        const amount = direction === 'next' ? -280 : 280
        this.container.scrollBy({ left: amount, behavior: 'smooth' })
    }

    renderVisibleStage = ({
        stage,
        stageKey,
        index,
        isCompleted,
        isCurrent,
        isFuture,
        canManage,
    }) => {
        const {
            canEditStages,
            canGoStages,
            onEditStage,
            onGoToStage,
            onViewDetails,
            readOnly,
        } = this.props
        const requiredPermissionGranted = !stage.requiredPermission
            || checkPermission(stage.requiredPermission)
        const canViewDetails = isCompleted || isCurrent
        const canGo = canGoStages && canManage && requiredPermissionGranted && !isCurrent
        const canEdit = canEditProcessStage(stage, this.props.userType, canEditStages)
            && canManage
            && requiredPermissionGranted
            && !isFuture
        const canOpenDetails = canViewDetails && typeof onViewDetails === 'function'
        const showActions = canOpenDetails || (!readOnly && (canGo || canEdit))

        return (
            <article
                className={[
                    'process-roadmap__card',
                    isCompleted ? 'is-completed' : '',
                    isCurrent ? 'is-current' : '',
                    isFuture ? 'is-future' : '',
                ].filter(Boolean).join(' ')}
            >
                <span className="process-roadmap__stage-number" aria-hidden="true">
                    {index + 1}
                </span>

                <h4 className="process-roadmap__stage-name">
                    {checkTranslation(stage.name)}
                </h4>

                {(isCompleted || isCurrent) && (
                    <span className="process-roadmap__status">
                        {checkTranslation(isCurrent ? '{{lang}}Current' : '{{lang}}Completed')}
                    </span>
                )}

                {showActions && (
                    <div className="process-roadmap__actions">
                        {canOpenDetails && (
                            <button
                                type="button"
                                className="process-roadmap__action process-roadmap__action--quiet"
                                onClick={() => onViewDetails(stageKey)}
                            >
                                {checkTranslation('{{lang}}Click to view')}
                            </button>
                        )}

                        {!readOnly && canGo && (
                            <button
                                type="button"
                                className="process-roadmap__action"
                                onClick={() => onGoToStage(stageKey)}
                            >
                                {checkTranslation('{{lang}}Go to this stage')}
                            </button>
                        )}

                        {!readOnly && canEdit && (
                            <button
                                type="button"
                                className="process-roadmap__action process-roadmap__action--secondary"
                                onClick={() => onEditStage(stageKey)}
                            >
                                {checkTranslation('{{lang}}Edit stage info')}
                            </button>
                        )}
                    </div>
                )}
            </article>
        )
    }

    renderRestrictedStage = ({ isCompleted, isCurrent, isFuture }) => (
        <div
            className={[
                'process-roadmap__restricted',
                isCompleted ? 'is-completed' : '',
                isCurrent ? 'is-current' : '',
                isFuture ? 'is-future' : '',
            ].filter(Boolean).join(' ')}
            role="img"
            aria-label={checkTranslation(
                isCurrent
                    ? '{{lang}}Restricted current stage'
                    : '{{lang}}Restricted process stage'
            )}
        >
            <span className="process-roadmap__restricted-core" aria-hidden="true" />
        </div>
    )

    render() {
        const {
            canAdvance,
            currentStageKey,
            nextStageKey,
            onClose,
            onNext,
            stages,
            userType,
            embedded = false,
            readOnly = false,
            titleId = 'process-roadmap-title',
        } = this.props
        const entries = Object.entries(stages || {})
        const currentIndex = entries.findIndex(([stageKey]) => stageKey === currentStageKey)
        const progress = currentIndex < 0 || entries.length === 0
            ? 0
            : Math.round(((currentIndex + 1) / entries.length) * 100)
        const canViewNextStage = nextStageKey
            ? canViewProcessStage(stages?.[nextStageKey], userType)
            : false

        return (
            <section
                className={[
                    'process-roadmap',
                    embedded ? 'is-embedded' : '',
                    readOnly ? 'is-read-only' : '',
                ].filter(Boolean).join(' ')}
                aria-labelledby={titleId}
            >
                <header className="process-roadmap__header">
                    <div>
                        <p className="process-roadmap__eyebrow">
                            {checkTranslation('{{lang}}Current Status')}
                        </p>
                        <h3 id={titleId} className="process-roadmap__title">
                            {checkTranslation('{{lang}}Process Roadmap')}
                        </h3>
                    </div>
                    <div
                        className="process-roadmap__progress"
                        aria-label={`${checkTranslation('{{lang}}Progress')}: ${progress}%`}
                    >
                        <span>{progress}%</span>
                        <div className="process-roadmap__progress-track" aria-hidden="true">
                            <div
                                className="process-roadmap__progress-value"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                </header>

                {!readOnly ? (
                    <div className="process-roadmap__toolbar">
                        <button
                            type="button"
                            className="process-roadmap__nav"
                            onClick={() => this.scroll('prev')}
                        >
                            {checkTranslation('{{lang}}Prev')}
                        </button>
                        <p>{checkTranslation('{{lang}}Restricted stages are shown without details')}</p>
                        <button
                            type="button"
                            className="process-roadmap__nav"
                            onClick={() => this.scroll('next')}
                        >
                            {checkTranslation('{{lang}}Next')}
                        </button>
                    </div>
                ) : (
                    <p className="process-roadmap__read-only-note">
                        {checkTranslation('{{lang}}Restricted stages are shown without details')}
                    </p>
                )}

                <div
                    ref={this.setContainerRef}
                    className="process-roadmap__viewport no-scrollbar"
                    role="list"
                    aria-label={checkTranslation('{{lang}}Process Roadmap')}
                >
                    {entries.map(([stageKey, stage], index) => {
                        const isCompleted = currentIndex >= 0 && index < currentIndex
                        const isCurrent = index === currentIndex
                        const isFuture = currentIndex < 0 || index > currentIndex
                        const canView = canViewProcessStage(stage, userType)
                        const canManage = canManageProcessStage(stage, userType)

                        return (
                            <React.Fragment key={stageKey}>
                                <div
                                    ref={isCurrent ? this.setCurrentRef : null}
                                    className={[
                                        'process-roadmap__item',
                                        canView ? 'is-visible' : 'is-restricted',
                                    ].join(' ')}
                                    role="listitem"
                                    aria-current={isCurrent ? 'step' : undefined}
                                >
                                    {canView
                                        ? this.renderVisibleStage({
                                            stage,
                                            stageKey,
                                            index,
                                            isCompleted,
                                            isCurrent,
                                            isFuture,
                                            canManage,
                                        })
                                        : this.renderRestrictedStage({
                                            isCompleted,
                                            isCurrent,
                                            isFuture,
                                        })}
                                </div>

                                {index < entries.length - 1 && (
                                    <div
                                        className={[
                                            'process-roadmap__connector',
                                            index < currentIndex ? 'is-completed' : '',
                                        ].filter(Boolean).join(' ')}
                                        aria-hidden="true"
                                    />
                                )}
                            </React.Fragment>
                        )
                    })}
                </div>

                {!readOnly && (
                    <footer className="process-roadmap__footer">
                        {nextStageKey && (
                            canAdvance ? (
                                <button
                                    type="button"
                                    className="process-roadmap__next"
                                    onClick={onNext}
                                >
                                    {canViewNextStage ? (
                                        <>
                                            <span>{checkTranslation('{{lang}}Go to Next Stage')}</span>
                                            <strong>
                                                {checkTranslation(stages[nextStageKey]?.name || nextStageKey)}
                                            </strong>
                                        </>
                                    ) : (
                                        <span>
                                            {checkTranslation('{{lang}}Complete Current Stage')}
                                        </span>
                                    )}
                                </button>
                            ) : (
                                <p className="process-roadmap__handoff">
                                    {checkTranslation('{{lang}}The next stage is handled by another role')}
                                </p>
                            )
                        )}
                        <button
                            type="button"
                            className="process-roadmap__close"
                            onClick={onClose}
                        >
                            {checkTranslation('{{lang}}Close')}
                        </button>
                    </footer>
                )}
            </section>
        )
    }
}

export default ProcessRoadmap
